<template>
  <div class="stock-snapshot-overview">
    <div class="level overview-heading">
      <div class="level-left">
        <div>
          <h1 class="title">Historische Bestände</h1>
          <p class="subtitle is-6">
            Unveränderliche Momentaufnahmen des Lagerbestands und seiner Bewertung
          </p>
        </div>
      </div>
      <div class="level-right">
        <b-field grouped>
          <p class="control">
            <b-button type="is-primary" icon-pack="fa" icon-left="plus" @click="openCreateModal">
              Snapshot erstellen
            </b-button>
          </p>
          <p class="control">
            <b-button
                type="is-primary"
                outlined
                icon-pack="fa"
                icon-left="sync-alt"
                :loading="isLoading"
                @click="fetchData">
              Aktualisieren
            </b-button>
          </p>
        </b-field>
      </div>
    </div>

    <b-notification
        v-if="loadError"
        type="is-danger"
        has-icon
        :closable="false">
      {{ loadError }}
    </b-notification>

    <b-table
        :data="snapshots"
        :loading="isLoading"
        :paginated="snapshots.length > perPage"
        :per-page="perPage"
        :current-page.sync="currentPage"
        pagination-position="bottom"
        default-sort="snapshot_at"
        default-sort-direction="desc"
        sort-icon="arrow-up"
        sort-icon-size="is-small"
        :row-class="snapshotRowClass"
        aria-next-label="Nächste Seite"
        aria-previous-label="Vorherige Seite"
        aria-page-label="Seite"
        aria-current-label="Aktuelle Seite">

      <b-table-column field="snapshot_at" label="Snapshot-Zeitpunkt" sortable v-slot="props">
        <span class="has-text-weight-semibold">{{ formatDateTime(props.row.snapshot_at) }}</span>
        <div class="is-size-7 has-text-grey">
          Erstellt: {{ formatDateTime(props.row.created_at) }}
        </div>
      </b-table-column>

      <b-table-column field="period" label="Periode" sortable searchable v-slot="props">
        {{ props.row.period || '–' }}
      </b-table-column>

      <b-table-column field="snapshot_type" label="Typ" sortable v-slot="props">
        <b-tag :type="snapshotTypeTag(props.row.snapshot_type)">
          {{ snapshotTypeLabel(props.row.snapshot_type) }}
        </b-tag>
      </b-table-column>

      <b-table-column field="status" label="Status" sortable v-slot="props">
        <b-tag :type="statusTag(props.row.status)" :class="{ 'status-creating': props.row.status === 'creating' }">
          <b-icon
              v-if="props.row.status === 'creating'"
              pack="fa"
              icon="spinner"
              size="is-small"
              custom-class="fa-spin" />
          {{ statusLabel(props.row.status) }}
        </b-tag>
        <b-tag v-if="props.row.integrity_valid === false" type="is-danger" class="integrity-tag">
          <b-icon pack="fa" icon="exclamation-triangle" size="is-small" />
          Integritätsfehler
        </b-tag>
      </b-table-column>

      <b-table-column field="position_count" label="Positionen" numeric sortable v-slot="props">
        {{ formatInteger(props.row.position_count) }}
      </b-table-column>

      <b-table-column field="total_value" label="Gesamtwert" numeric sortable v-slot="props">
        <span v-if="props.row.total_value !== null && props.row.total_value !== undefined" class="has-text-weight-semibold">
          {{ formatMoney(props.row.total_value) }}
        </span>
        <span v-else>–</span>
      </b-table-column>

      <b-table-column label="Qualität" centered v-slot="props">
        <b-tooltip v-if="hasQualityData(props.row.quality)" :label="qualityTooltip(props.row.quality)" multilined>
          <b-tag :type="qualityTag(props.row.quality)">
            <b-icon
                pack="fa"
                :icon="qualityIssueCount(props.row.quality) === 0 ? 'check' : 'exclamation-triangle'"
                size="is-small" />
            {{ qualityIssueCount(props.row.quality) === 0 ? 'Ohne Befund' : qualityIssueCount(props.row.quality) + ' Hinweise' }}
          </b-tag>
        </b-tooltip>
        <b-tag v-else type="is-light">Keine Angaben</b-tag>
      </b-table-column>

      <b-table-column label="Aktionen" centered width="90" v-slot="props">
        <b-tooltip label="Snapshot öffnen" position="is-left">
          <b-button
              type="is-primary"
              outlined
              icon-pack="fa"
              icon-left="eye"
              aria-label="Snapshot öffnen"
              @click="openSnapshot(props.row)" />
        </b-tooltip>
      </b-table-column>

      <template #empty>
        <section class="section has-text-centered has-text-grey">
          <b-icon pack="fa" icon="archive" size="is-large" />
          <p>Noch keine historischen Bestände vorhanden.</p>
        </section>
      </template>
    </b-table>

    <b-field class="page-size" label="Einträge je Seite" horizontal>
      <b-select v-model="perPage" size="is-small">
        <option :value="10">10</option>
        <option :value="20">20</option>
        <option :value="50">50</option>
      </b-select>
    </b-field>

    <b-modal :active.sync="isCreateModalActive" has-modal-card trap-focus :can-cancel="!isCreating">
      <form class="modal-card" @submit.prevent="createSnapshot">
        <header class="modal-card-head">
          <p class="modal-card-title">Snapshot erstellen</p>
        </header>
        <section class="modal-card-body">
          <b-message type="is-info" has-icon>
            Ein Snapshot wird dauerhaft gespeichert. Abgeschlossene Snapshots können nicht geändert oder gelöscht werden.
          </b-message>

          <b-field label="Typ">
            <p class="control">
              <b-tag type="is-light" size="is-medium">Manuell</b-tag>
            </p>
          </b-field>

          <b-field label="Kommentar (optional)">
            <b-input
                v-model.trim="createForm.comment"
                type="textarea"
                maxlength="500"
                placeholder="Anlass oder fachlicher Hinweis zum Snapshot" />
          </b-field>

          <b-field label="Ersetzt ungültigen Snapshot (optional)">
            <b-select v-model="createForm.replaces_snapshot_id" expanded>
              <option :value="null">Keinen Snapshot ersetzen</option>
              <option v-for="snapshot in invalidSnapshots" :key="snapshot.id" :value="snapshot.id">
                #{{ snapshot.id }} – {{ formatDateTime(snapshot.snapshot_at) }}
              </option>
            </b-select>
          </b-field>
          <p v-if="invalidSnapshots.length === 0" class="help">
            In der aktuellen Liste ist kein ungültiger Snapshot verfügbar.
          </p>
        </section>
        <footer class="modal-card-foot">
          <b-button :disabled="isCreating" @click="isCreateModalActive = false">Abbrechen</b-button>
          <b-button
              native-type="submit"
              type="is-primary"
              :loading="isCreating">
            Snapshot erstellen
          </b-button>
        </footer>
      </form>
    </b-modal>
  </div>
</template>

<script>
import apiservice from '@/services/apiservice'
import { formatMoney } from '@/services/decimal-format'

const QUALITY_FIELDS = [
  'missing_cost_price_count',
  'negative_stock_count',
  'validation_error_count',
  'warning_count'
]

export default {
  name: 'StockSnapshotOverview',
  data() {
    return {
      snapshots: [],
      isLoading: false,
      isCreating: false,
      loadError: '',
      currentPage: 1,
      perPage: 20,
      isCreateModalActive: false,
      createForm: this.emptyCreateForm()
    }
  },
  computed: {
    invalidSnapshots() {
      return this.snapshots.filter(snapshot => snapshot.status === 'invalid')
    }
  },
  beforeMount() {
    this.fetchData()
  },
  methods: {
    emptyCreateForm() {
      return {
        comment: '',
        replaces_snapshot_id: null
      }
    },
    fetchData() {
      this.isLoading = true
      this.loadError = ''

      apiservice()
        .get('stock/snapshots')
        .then(response => {
          this.snapshots = Array.isArray(response.data.data) ? response.data.data : []
        })
        .catch(() => {
          this.loadError = 'Die historischen Bestände konnten nicht geladen werden.'
        })
        .then(() => {
          this.isLoading = false
        })
    },
    openCreateModal() {
      this.createForm = this.emptyCreateForm()
      this.isCreateModalActive = true
    },
    createSnapshot() {
      const payload = {
        snapshot_type: 'manual'
      }
      if (this.createForm.comment) {
        payload.comment = this.createForm.comment
      }
      if (this.createForm.replaces_snapshot_id !== null) {
        payload.replaces_snapshot_id = this.createForm.replaces_snapshot_id
      }

      this.isCreating = true
      apiservice()
        .post('stock/snapshots', payload)
        .then(() => {
          this.isCreateModalActive = false
          this.$buefy.toast.open({
            message: 'Snapshot wurde erstellt.',
            type: 'is-success',
            position: 'is-bottom'
          })
          this.fetchData()
        })
        .catch(error => {
          const invalidSnapshot = error && error.data && error.data.status === 'invalid'
          this.$buefy.dialog.alert({
            title: invalidSnapshot ? 'Snapshot ist ungültig' : 'Snapshot konnte nicht erstellt werden',
            message: invalidSnapshot
              ? `Der Snapshot #${error.data.id} wurde dauerhaft als ungültig gespeichert: ${error.data.invalid_reason || 'Fachliche Validierung fehlgeschlagen.'}`
              : this.apiErrorMessage(error),
            type: 'is-danger',
            hasIcon: true,
            icon: 'exclamation-triangle',
            iconPack: 'fa'
          })
          if (invalidSnapshot) {
            this.isCreateModalActive = false
            this.fetchData()
          }
        })
        .then(() => {
          this.isCreating = false
        })
    },
    openSnapshot(snapshot) {
      this.$router.push(`/stock/snapshots/${snapshot.id}`)
    },
    snapshotRowClass(row) {
      if (row.integrity_valid === false) {
        return 'has-integrity-error'
      }
      return row.status === 'invalid' ? 'is-invalid-snapshot' : ''
    },
    snapshotTypeLabel(type) {
      return type === 'monthly' ? 'Monatsabschluss' : type === 'manual' ? 'Manuell' : type
    },
    snapshotTypeTag(type) {
      return type === 'monthly' ? 'is-info is-light' : 'is-light'
    },
    statusLabel(status) {
      return status === 'creating'
        ? 'Wird erstellt'
        : status === 'completed'
          ? 'Abgeschlossen'
          : status === 'invalid'
            ? 'Ungültig'
            : status
    },
    statusTag(status) {
      return status === 'completed'
        ? 'is-success'
        : status === 'invalid'
          ? 'is-danger'
          : 'is-info'
    },
    qualityValue(quality, field) {
      const value = quality && quality[field]
      return typeof value === 'number' ? value : null
    },
    hasQualityData(quality) {
      return QUALITY_FIELDS.some(field => this.qualityValue(quality, field) !== null)
    },
    qualityIssueCount(quality) {
      return QUALITY_FIELDS.reduce((sum, field) => sum + (this.qualityValue(quality, field) || 0), 0)
    },
    qualityTag(quality) {
      if (
        this.qualityValue(quality, 'missing_cost_price_count') > 0 ||
        this.qualityValue(quality, 'validation_error_count') > 0
      ) {
        return 'is-danger'
      }
      return this.qualityIssueCount(quality) > 0 ? 'is-warning' : 'is-success is-light'
    },
    qualityTooltip(quality) {
      return [
        `Fehlende Einkaufspreise: ${this.qualityValue(quality, 'missing_cost_price_count')}`,
        `Negative Bestände: ${this.qualityValue(quality, 'negative_stock_count')}`,
        `Validierungsfehler: ${this.qualityValue(quality, 'validation_error_count')}`,
        `Warnungen: ${this.qualityValue(quality, 'warning_count')}`
      ].join(' · ')
    },
    formatDateTime(value) {
      if (!value) {
        return '–'
      }
      const date = new Date(value)
      return Number.isNaN(date.getTime()) ? value : date.toLocaleString('de-DE')
    },
    formatInteger(value) {
      return typeof value === 'number' ? value.toLocaleString('de-DE') : value || '–'
    },
    formatMoney,
    apiErrorMessage(error) {
      if (error && typeof error.message === 'string') {
        return error.message
      }
      if (error && error.data && typeof error.data.message === 'string') {
        return error.data.message
      }
      return 'Bitte Eingaben und Berechtigung prüfen und erneut versuchen.'
    }
  }
}
</script>

<style scoped>
.overview-heading {
  align-items: flex-start;
  margin-bottom: 1.5rem;
}

.overview-heading .subtitle {
  margin-top: -0.75rem;
}

.integrity-tag {
  display: table;
  margin-top: 0.35rem;
}

.page-size {
  margin-top: 0.75rem;
  max-width: 15rem;
}

form.modal-card {
  width: min(92vw, 640px);
}

::v-deep tr.has-integrity-error td {
  background-color: #fff5f7;
}

::v-deep tr.is-invalid-snapshot td {
  background-color: #fffaf0;
}

@media screen and (max-width: 768px) {
  .overview-heading {
    display: block;
  }

  .overview-heading .level-right {
    margin-top: 1rem;
  }
}
</style>
